import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE } from "../../utils/api";
import { toast, ToastContainer } from "react-toastify";
import PageHeader from "../../components/common/PageHeader";
import SearchBar from "../../components/common/SearchBar";
import { useRestaurantDetails } from "../../context/RestaurantDetailsContext";
import Pagination from "../../components/Pagination";

const ComboList = () => {
  const [combos, setCombos] = useState([]);
  const { restaurantDetails } = useRestaurantDetails();
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [comboToDelete, setComboToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(undefined);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCombos();
  }, [page, searchQuery]);

  const fetchCombos = async () => {
    try {
      const response = await api.get("/combos", {
        params: {
          page,
          limit,
          search: searchQuery?.trim() ? searchQuery.trim() : undefined,
        },
      });

      // API returns paginated payload { combos, totalPages, ... }
      const payload = response.data;
      setCombos(payload?.combos || []);
      setTotalPages(payload?.totalPages || 1);

      // If backend decides limit (default 10), capture it so UI stays consistent.
      if (payload?.limit && payload.limit !== limit) setLimit(payload.limit);
    } catch (error) {
      toast.error("Failed to fetch combos");
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await api.patch(`/combos/${id}/toggle-visibility`, {
        isVisible: !currentStatus,
      });
      fetchCombos();
      toast.success("Combo status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const openDeleteModal = (combo, e) => {
    e.stopPropagation();
    setComboToDelete(combo);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!comboToDelete) return;
    try {
      await api.delete(`/combos/${comboToDelete._id}`);
      toast.success("Combo deleted successfully");
      fetchCombos();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete combo");
    } finally {
      setDeleteModalOpen(false);
      setComboToDelete(null);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const getComboItemsForDisplay = (combo) => {
    if (Array.isArray(combo?.comboProductItems) && combo.comboProductItems.length) {
      return combo.comboProductItems
        .map((x) => ({
          product: x?.product,
          qty: x?.qty || 1,
        }))
        .filter((x) => x.product);
    }
    return (combo?.comboProducts || []).map((p) => ({ product: p, qty: 1 }));
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-gray-50 via-white to-gray-50 px-2 py-3 overflow-hidden">
      <ToastContainer position="top-center" autoClose={3000} />
      
      {/* Header Section */}
      <div className="flex-shrink-0">
        <PageHeader
          showBackButton={true}
          backTo={-1}
          title="Combos"
          subtitle={
            <span className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
              Manage your combo items
            </span>
          }
          action={
            <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 mt-3 sm:mt-0 w-full">
              <button
                title="Create New Combo"
                onClick={() => navigate("/combos/add")}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-3 py-2 sm:px-4 rounded-lg transition-colors duration-200 flex items-center gap-2 shadow-sm hover:shadow-md"
              >
                <svg
                  className="hidden sm:block w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <span className="hidden sm:inline">Create New Combo</span>
                <span className="sm:hidden text-xs font-semibold">Create</span>
              </button>
            </div>
          }
          search={
            <SearchBar
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search combos by name..."
              className="max-w-lg"
            />
          }
        />
      </div>

      {/* Table Section */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="flex-1 overflow-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-black sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Image
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Combo Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Products
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {combos.map((combo) => (
                  <tr
                    key={combo._id}
                    onClick={() => navigate(`/combos/edit/${combo._id}`)}
                    className="h-[50px] hover:bg-gradient-to-r hover:from-gray-50 hover:to-white transition-all duration-200 cursor-pointer hover:shadow-sm"
                  >
                    <td className="px-4 py-2">
                      <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                        {combo.imageUrl ? (
                          <img
                            src={`${API_BASE}${combo.imageUrl}`}
                            alt={combo.productName?.en || "Combo"}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className="font-semibold text-gray-900 text-xs">
                        {combo.productName?.en || combo.productName}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="text-gray-700 text-xs flex flex-col gap-1">
                        {getComboItemsForDisplay(combo).map((x, i) => (
                          <span key={i}>
                            - {(x.product?.productName?.en || x.product?.productName || "Unknown")}{" "}
                            <span className="text-gray-500">x{x.qty}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className="text-gray-900 font-medium text-xs">
                        {restaurantDetails?.currency?.symbol || "₹"}
                        {combo.price}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className="text-gray-600 text-xs line-clamp-2">
                        {combo.description?.en || combo.description || "No description"}
                      </span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={combo.isVisible || false}
                          onChange={() => handleToggle(combo._id, combo.isVisible)}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        <span className="ml-2 text-xs font-semibold text-gray-800">
                          {combo.isVisible ? "Enabled" : "Disabled"}
                        </span>
                      </label>
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => openDeleteModal(combo, e)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-full transition-colors"
                        title="Delete Combo"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
                {combos.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      No Combos Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4 transform transition-all">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">Delete Combo</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete <span className="font-semibold text-gray-700">{comboToDelete?.productName?.en || comboToDelete?.productName}</span>? This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  onClick={() => setDeleteModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium shadow-sm"
                  onClick={confirmDelete}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComboList;
